/**
 * 소리를 코드로 만든다: 북·징·꽹과리·나발·화살 바람. 파일이 없어 받는 것이 0이고 허가를 따질 것도 없다.
 *
 * 전장의 신호를 우리 타악기로 잡았다 — 북은 나아가라, 징은 멈춰 버텨라, 나발은 알림이다. 진짜 악기를 흉내 낸 것이
 * 아니라 그 구실을 하는 소리다: 북은 음이 떨어지는 낮은 사인파에 채가 닿는 잡음을 얹고, 징은 배음이 어긋난 여러
 * 사인파를 길게 끌며 음을 살짝 올린다(징은 치고 나면 소리가 올라간다).
 *
 * **만든 쪽은 이 소리를 들어 보지 못한다. 고른 것은 플레이한 사람이다**(2026-10-08): 소리마다 합성 후보 다섯과 CC0 녹음
 * 넷쯤을 나란히 들어 보고 하나씩 골랐다 — 열한 가지 모두 합성이 뽑혔다. 기준이 된 것은 함성이다: 화면의 파동이
 * 누르자마자 훅 퍼지므로 소리도 힘을 맨 앞에 모은다(큰 북을 겹쳐 치고, 터지는 잡음이 멀어진다). 처음의 함성은 북 셋
 * 뒤에 나발 한 음을 길게 끌었는데, 뒤로 갈수록 어두워지고 작아져 "아래로 처진다"는 말을 들었다. 이김·장수가 쓰러짐도
 * 같은 결로 골랐다. 길이와 크기는 `tools/sound-peaks.ts`가 잰다.
 *
 * 소리를 바꿀 때는 이 파일의 조리법만 고치면 된다 — 부르는 쪽은 `CueId`만 안다.
 * `BaseAudioContext`만 쓴다: 실제 재생(`AudioContext`)과 크기 재기(`OfflineAudioContext`)가 같은 조리법을 돈다.
 */
import type { CueId } from './cues.ts';

type Ctx = BaseAudioContext;
type Recipe = (ctx: Ctx, out: AudioNode, t: number) => void;

const noiseOf = new WeakMap<Ctx, AudioBuffer>();

/** 흰 잡음 1초. 컨텍스트마다 한 번 만든다. 난수는 고정된 씨앗 — 잴 때마다 같은 파형이 나와야 크기를 견줄 수 있다. */
function noise(ctx: Ctx): AudioBuffer {
  let buf = noiseOf.get(ctx);
  if (buf) return buf;
  buf = ctx.createBuffer(1, ctx.sampleRate, ctx.sampleRate);
  const data = buf.getChannelData(0);
  let s = 0x2f6e2b1;
  for (let i = 0; i < data.length; i++) {
    s = (Math.imul(s, 1664525) + 1013904223) | 0;
    data[i] = (s >>> 8) / 0x800000 - 1;
  }
  noiseOf.set(ctx, buf);
  return buf;
}

/** 0에서 `peak`까지 `attack`초에 올리고 `decay`초에 걸쳐 잦아든다. 0에서 바로 튀면 딱 소리가 난다. */
function shape(g: GainNode, t: number, peak: number, attack: number, decay: number): void {
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(peak, t + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, t + attack + decay);
}

function burst(ctx: Ctx, out: AudioNode, t: number, type: BiquadFilterType, freq: number, peak: number, dur: number, q = 1): void {
  const src = ctx.createBufferSource();
  src.buffer = noise(ctx);
  const f = ctx.createBiquadFilter();
  f.type = type;
  f.frequency.value = freq;
  f.Q.value = q;
  const g = ctx.createGain();
  shape(g, t, peak, 0.002, dur);
  src.connect(f).connect(g).connect(out);
  src.start(t);
  src.stop(t + dur + 0.05);
}

/** 북: 음이 떨어지는 낮은 사인파 + 채가 가죽에 닿는 잡음. */
function drum(ctx: Ctx, out: AudioNode, t: number, f = 92, peak = 0.8, decay = 0.36): void {
  const o = ctx.createOscillator();
  o.type = 'sine';
  o.frequency.setValueAtTime(f * 2.1, t);
  o.frequency.exponentialRampToValueAtTime(f, t + 0.07);
  o.frequency.exponentialRampToValueAtTime(f * 0.82, t + decay);
  const g = ctx.createGain();
  shape(g, t, peak, 0.004, decay);
  o.connect(g).connect(out);
  o.start(t);
  o.stop(t + decay + 0.08);
  burst(ctx, out, t, 'lowpass', 1100, peak * 0.3, 0.04);
}

const GONG_PARTIALS: readonly (readonly [number, number])[] = [[1, 1], [1.48, 0.55], [2.07, 0.42], [2.74, 0.3], [3.63, 0.18], [4.21, 0.1]];

/** 징: 배음이 어긋난 사인파들을 길게 끈다. `bend`만큼 음이 올라간다(낮은 징은 1보다 작게 주어 가라앉힌다). */
function gong(ctx: Ctx, out: AudioNode, t: number, f = 150, peak = 0.34, decay = 2.0, bend = 1.035): void {
  for (let i = 0; i < GONG_PARTIALS.length; i++) {
    const [ratio, weight] = GONG_PARTIALS[i];
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(f * ratio, t);
    o.frequency.exponentialRampToValueAtTime(f * ratio * bend, t + Math.min(0.6, decay * 0.4));
    const g = ctx.createGain();
    // 높은 배음일수록 빨리 꺼진다 — 그래야 친 순간은 쨍하고 꼬리는 둥글다.
    const d = decay * (1 - i * 0.13);
    shape(g, t, peak * weight, 0.006, d);
    o.connect(g).connect(out);
    o.start(t);
    o.stop(t + d + 0.08);
  }
  burst(ctx, out, t, 'bandpass', 520, peak * 0.5, 0.05);
}

/** 꽹과리: 높고 짧은 쇳소리. */
function kkwaeng(ctx: Ctx, out: AudioNode, t: number, peak = 0.2, decay = 0.22): void {
  const partials: readonly (readonly [number, number])[] = [[1, 1], [1.41, 0.7], [2.29, 0.5], [3.12, 0.3]];
  for (const [ratio, weight] of partials) {
    const o = ctx.createOscillator();
    o.type = 'triangle';
    o.frequency.value = 820 * ratio;
    const g = ctx.createGain();
    shape(g, t, peak * weight, 0.002, decay);
    o.connect(g).connect(out);
    o.start(t);
    o.stop(t + decay + 0.05);
  }
}

/**
 * 굵은 나발: 살짝 어긋난 톱니파 둘에 한 옥타브 아래 사인파를 깔고, 숨을 불어 넣듯 `swell`초에 부풀린다. 음은 아래에서
 * 밀어 올리고, 거르개를 열었다가 `endCut`까지 닫는다(높게 두면 끝까지 밝다). 크기는 길이의 `hold`까지 버틴 뒤 잦아든다.
 * `to`를 주면 그 음으로 미끄러진다. 숨소리(잡음)를 조금 섞는다.
 *
 * 처음의 나발은 톱니파 하나를 거르개에 통과시킨 한 음이었다 — 가늘었고, 길게 끌면 어두워지며 처졌다.
 */
function horn(ctx: Ctx, out: AudioNode, t: number, f: number, dur: number, peak: number, swell: number, to: number, endCut: number, hold: number): void {
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.Q.value = 1.6;
  lp.frequency.setValueAtTime(260, t);
  lp.frequency.linearRampToValueAtTime(1300, t + swell + dur * 0.2);
  lp.frequency.linearRampToValueAtTime(endCut, t + dur);
  const g = ctx.createGain();
  g.gain.setValueAtTime(0.0001, t);
  g.gain.linearRampToValueAtTime(peak, t + swell);
  g.gain.setValueAtTime(peak, t + dur * hold);
  g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  lp.connect(g).connect(out);
  const voices: readonly (readonly [number, OscillatorType, number])[] = [[1, 'sawtooth', 0.5], [1.006, 'sawtooth', 0.5], [0.5, 'sine', 0.55]];
  for (const [mul, type, w] of voices) {
    const o = ctx.createOscillator();
    o.type = type;
    o.frequency.setValueAtTime(f * mul * 0.93, t);
    o.frequency.exponentialRampToValueAtTime(f * mul, t + 0.16);
    if (to !== f) o.frequency.exponentialRampToValueAtTime(to * mul, t + dur);
    const og = ctx.createGain();
    og.gain.value = w;
    o.connect(og).connect(lp);
    o.start(t);
    o.stop(t + dur + 0.05);
  }
  const src = ctx.createBufferSource();
  src.buffer = noise(ctx);
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.frequency.value = 900;
  bp.Q.value = 0.8;
  const ng = ctx.createGain();
  shape(ng, t, peak * 0.12, swell, dur - swell);
  src.connect(bp).connect(ng).connect(out);
  src.start(t);
  src.stop(t + dur + 0.05);
}

/** 화살 바람: 잡음의 띠를 `from`에서 `to`로 훑는다. */
function whoosh(ctx: Ctx, out: AudioNode, t: number, peak: number, dur: number, from: number, to: number): void {
  const src = ctx.createBufferSource();
  src.buffer = noise(ctx);
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.Q.value = 1.4;
  bp.frequency.setValueAtTime(from, t);
  bp.frequency.exponentialRampToValueAtTime(to, t + dur);
  const g = ctx.createGain();
  shape(g, t, peak, dur * 0.3, dur * 0.7);
  src.connect(bp).connect(g).connect(out);
  src.start(t);
  src.stop(t + dur + 0.05);
}

/** 퍼져 나가는 파동: 잡음의 띠가 높은 데서 낮은 데로 빠르게 내려가며 잦아든다 — 가까이서 터져 멀어지는 소리. */
function shock(ctx: Ctx, out: AudioNode, t: number, peak: number, dur: number): void {
  const src = ctx.createBufferSource();
  src.buffer = noise(ctx);
  const bp = ctx.createBiquadFilter();
  bp.type = 'bandpass';
  bp.Q.value = 0.9;
  bp.frequency.setValueAtTime(2600, t);
  bp.frequency.exponentialRampToValueAtTime(280, t + dur);
  const g = ctx.createGain();
  shape(g, t, peak, 0.004, dur);
  src.connect(bp).connect(g).connect(out);
  src.start(t);
  src.stop(t + dur + 0.05);
}

function blip(ctx: Ctx, out: AudioNode, t: number, f: number, peak: number, decay: number): void {
  const o = ctx.createOscillator();
  o.type = 'sine';
  o.frequency.value = f;
  const g = ctx.createGain();
  shape(g, t, peak, 0.003, decay);
  o.connect(g).connect(out);
  o.start(t);
  o.stop(t + decay + 0.03);
}

/**
 * 일마다의 소리. 조리법을 고치면 `cues.ts`의 `durMs`(들리는 길이)를 다시 잰 값에 맞춘다(동시 발음 수를 그것으로 센다).
 * 잡음은 1초짜리라, 그보다 길게 건 바람·숨소리는 1초에서 끝난다(그때는 이미 작다) — 긴 바람이 필요하면 버퍼를 늘린다.
 * 크기는 `tools/sound-peaks.ts`로 잰다 — 하나씩은 0.9를 넘지 않게, 겹쳐도 깨지지 않게 뒤쪽에 누르개를 둔다.
 */
export const RECIPES: Readonly<Record<CueId, Recipe>> = {
  // 단추: 나무를 톡 치는 짧은 소리. 자주 나므로 작고 짧게.
  'ui.tap': (ctx, out, t) => {
    blip(ctx, out, t, 620, 0.16, 0.035);
    burst(ctx, out, t, 'highpass', 2400, 0.05, 0.012);
  },
  // 소리를 켰다: 두 음이 올라간다 — 켠 크기를 바로 들려준다.
  'ui.on': (ctx, out, t) => {
    blip(ctx, out, t, 520, 0.22, 0.09);
    blip(ctx, out, t + 0.1, 780, 0.22, 0.14);
  },
  // 함성: 누르는 순간 터진다. 큰 북 둘을 겹쳐 치고(낮은 것과 그 위), 터지는 잡음이 높은 데서 낮은 데로 내려가며
  // 멀어진다. 나발은 기다리지 않고 함께 터져 짧게 끊고, 작은 북 하나가 여운으로 뒤따른다.
  'skill.roar': (ctx, out, t) => {
    drum(ctx, out, t, 68, 0.55, 0.55);
    drum(ctx, out, t, 118, 0.34, 0.22);
    shock(ctx, out, t, 0.34, 0.5);
    horn(ctx, out, t, 220, 0.62, 0.2, 0.025, 226, 900, 0.35);
    drum(ctx, out, t + 0.3, 92, 0.3, 0.3);
  },
  // 방진: 쿵, 쿵. 무거운 북 두 번에 쇠가 부딪는 소리 — 징 없이 단단하게.
  'skill.phalanx': (ctx, out, t) => {
    drum(ctx, out, t, 70, 0.62, 0.4);
    drum(ctx, out, t + 0.24, 62, 0.7, 0.7);
    burst(ctx, out, t + 0.24, 'bandpass', 1500, 0.12, 0.06, 2);
  },
  // 일제사격: 화살 떼. 화살 바람 여섯이 촘촘히 날아간다(뒤로 갈수록 작고 길고 높게).
  'skill.volley': (ctx, out, t) => {
    const starts = [0, 0.05, 0.11, 0.16, 0.23, 0.31];
    for (let i = 0; i < starts.length; i++) whoosh(ctx, out, t + starts[i], 0.8 - i * 0.07, 0.3 + i * 0.03, 700 + i * 90, 3000 + i * 200);
  },
  // 동료 기술: 꽹과리 둘.
  'mate.skill': (ctx, out, t) => {
    kkwaeng(ctx, out, t, 0.26);
    kkwaeng(ctx, out, t + 0.14, 0.3, 0.3);
  },
  // 적장이 쓰러졌다: 밝은 징에 북 둘.
  'chief.fall': (ctx, out, t) => {
    gong(ctx, out, t, 205, 0.3, 2.4, 1.05);
    drum(ctx, out, t + 0.05, 100, 0.6);
    drum(ctx, out, t + 0.3, 100, 0.7, 0.45);
  },
  // 적 부장이 쓰러졌다: 두둥. 북 두 번을 빠르게.
  'sub.fall': (ctx, out, t) => {
    drum(ctx, out, t, 112, 0.5, 0.2);
    drum(ctx, out, t + 0.13, 88, 0.65, 0.45);
  },
  // 장수가 쓰러졌다: 무너진다. 첫머리에 크게 터지고, 낮은 징과 바람 소리가 길게 가라앉는다.
  'hero.fall': (ctx, out, t) => {
    drum(ctx, out, t, 58, 0.6, 0.9);
    shock(ctx, out, t, 0.28, 1.4);
    gong(ctx, out, t + 0.03, 92, 0.22, 2.6, 0.93);
  },
  // 이김: 함성처럼 터지고, 굵은 나발 두 음이 올라가 밝은 채로 길게 버틴다.
  win: (ctx, out, t) => {
    drum(ctx, out, t, 66, 0.5, 0.6);
    drum(ctx, out, t, 114, 0.3, 0.24);
    shock(ctx, out, t, 0.24, 0.45);
    horn(ctx, out, t + 0.05, 196, 0.4, 0.2, 0.05, 196, 1000, 0.85);
    horn(ctx, out, t + 0.42, 294, 1.5, 0.24, 0.08, 300, 1250, 0.85);
    drum(ctx, out, t + 0.42, 84, 0.5, 0.5);
  },
  // 짐: 느린 북 하나에 굵은 나발이 길게 내려간다(어두워지며 잦아든다).
  lose: (ctx, out, t) => {
    drum(ctx, out, t, 66, 0.6, 0.9);
    horn(ctx, out, t + 0.1, 196, 2.4, 0.22, 0.25, 123, 280, 0.45);
  },
};
